import { useEffect, useState } from 'react';
import {
  getMembershipPricing,
  type MembershipInterval,
  type MembershipPricing,
  type MembershipTier,
  type Program,
} from '@/lib/api';
import { FieldError } from './FieldError';
import { formatPhoneInput } from './WaiversStep';

const PROGRAM_LABEL: Record<Program, string> = {
  Boxing: 'Boxing',
  MuayThai: 'Muay Thai',
  MMA: 'MMA',
  BJJ: 'Brazilian Jiu-Jitsu',
  Kids: 'Kids',
};

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

export function ConfirmStep(props: {
  tier: MembershipTier | null;
  athletes: boolean;
  interval: MembershipInterval;
  setInterval: (i: MembershipInterval) => void;
  programs: Program[];
  email: string;
  phone: string;
  setPhone: (s: string) => void;
  submitting: boolean;
  submitError: string | null;
  phoneError?: string | null;
  clearPhoneError?: () => void;
}) {
  const [pricing, setPricing] = useState<MembershipPricing | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);

  // Fetch pricing whenever the tier or athletes flag changes. The endpoint
  // is read-only and idempotent, so we don't bother debouncing.
  useEffect(() => {
    if (!props.tier) {
      setPricing(null);
      return;
    }
    let cancelled = false;
    setPricingError(null);
    getMembershipPricing({ tier: props.tier, athletes: props.athletes })
      .then((p) => {
        if (cancelled) return;
        setPricing(p);
        // If the user previously selected YEARLY but the new tier has no yearly
        // SKU (e.g. KIDS / athlete), force back to MONTHLY.
        if (p.yearly === null && props.interval !== 'MONTHLY') {
          props.setInterval('MONTHLY');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setPricingError(String((e as Error).message ?? e));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tier, props.athletes]);

  const yearlyAvailable = pricing?.yearly !== null && pricing?.yearly !== undefined;
  // The yearly plan is billed MONTHLY at a discounted committed rate (a 12-month
  // commitment), NOT an up-front lump sum. So `yearly.amountCents` IS the
  // monthly charge, and savings are measured per month vs the month-to-month rate.
  const yearlyMonthlyCents = pricing && pricing.yearly !== null ? pricing.yearly.amountCents : 0;
  const yearlySavingsPerMonth =
    pricing && pricing.yearly !== null ? pricing.monthly.amountCents - yearlyMonthlyCents : 0;
  const hasPhone = props.phone.replace(/\D/g, '').length >= 7;

  return (
    <div className="grid gap-4 text-sm">
      <p>
        Plan: <strong>{props.programs.map((p) => PROGRAM_LABEL[p]).join(', ')}</strong>
        {props.athletes ? ' · athlete pricing' : ''}
      </p>
      <p>
        Email: <strong>{props.email}</strong>
      </p>

      <fieldset className="grid gap-3" data-testid="interval-picker">
        <legend className="mb-1 text-xs uppercase tracking-wider text-bone/60">Billing plan</legend>
        {pricingError && (
          <div
            role="alert"
            className="rounded border border-accent/50 bg-accent/10 p-3 text-xs text-accent"
          >
            Couldn&apos;t load pricing. Please refresh and try again.
          </div>
        )}
        {pricing === null && !pricingError && (
          <p className="text-xs text-bone/60">Loading pricing…</p>
        )}
        {pricing && (
          <div className={`grid gap-3 ${yearlyAvailable ? 'sm:grid-cols-2' : ''}`}>
            <PlanCard
              label="Monthly"
              priceLine={`${dollars(pricing.monthly.amountCents)} / month`}
              selected={props.interval === 'MONTHLY'}
              onClick={() => props.setInterval('MONTHLY')}
            />
            {yearlyAvailable && pricing.yearly && (
              <PlanCard
                // Not "Yearly" — members read that as one up-front payment for
                // the year, when it is 12 monthly charges at a lower rate.
                label="12-Month Program"
                priceLine={`${dollars(yearlyMonthlyCents)} / month`}
                sub={
                  yearlySavingsPerMonth > 0
                    ? `billed monthly for 12 months · save ${dollars(yearlySavingsPerMonth)}/mo`
                    : 'billed monthly for 12 months'
                }
                selected={props.interval === 'YEARLY'}
                onClick={() => props.setInterval('YEARLY')}
              />
            )}
          </div>
        )}
      </fieldset>

      {!hasPhone && (
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">Phone</span>
          <input
            id="field-phone"
            type="tel"
            required
            value={props.phone}
            onChange={(e) => {
              const formatted = formatPhoneInput(e.target.value);
              props.setPhone(formatted);
              if (formatted.replace(/\D/g, '').length >= 7) props.clearPhoneError?.();
            }}
            className="input"
            autoComplete="tel"
            placeholder="(972) 555-0123 or +44 20 7123 4567"
          />
          <FieldError message={props.phoneError} />
        </label>
      )}

      {props.submitting && <p className="text-bone/60">Redirecting to Stripe…</p>}
      {props.submitError && (
        <div role="alert" className="rounded border border-accent/50 bg-accent/10 p-3 text-accent">
          {props.submitError}
        </div>
      )}
    </div>
  );
}

function PlanCard({
  label,
  priceLine,
  sub,
  selected,
  onClick,
}: {
  label: string;
  priceLine: string;
  sub?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition-colors ${
        selected
          ? 'border-accent bg-accent/10'
          : 'border-bone/15 bg-neutral-900 hover:border-bone/30'
      }`}
    >
      <div className="font-display text-2xl">{label}</div>
      <div className="mt-1 text-sm text-bone/90">{priceLine}</div>
      {sub && <div className="mt-1 text-xs text-bone/60">{sub}</div>}
    </button>
  );
}
